import Axios from "axios";
import {
  GetProvidersRequest,
  GetAllProvidersRequest,
  ProvidersResponse,
} from "../pages/DealerById/components/DealerAcquirersTable/types";
import type {
  CreateProviderApiResponse,
  CreateProviderResponseData,
  ProviderAdditionalStep,
  ProviderAdditionalStepPayload,
} from "./paymentApi.types";

function normalizeAdditionalStep(
  step?: ProviderAdditionalStepPayload,
): ProviderAdditionalStep | undefined {
  if (!step) {
    return undefined;
  }

  if (typeof step === "string") {
    return { linkLabel: step.trim() };
  }

  return step;
}

const api = Axios.create({
  baseURL: process.env.REACT_APP_PAYMENT_API_URL,
  timeout: 15000,
});

export const getProviders = async ({
  merchantId,
}: GetProvidersRequest): Promise<ProvidersResponse> => {
  const response = await api.get<ProvidersResponse>(
    `/v1/merchants/${merchantId}/providers`,
  );
  return response.data;
};

export const getAllProviders = async ({
  types,
}: GetAllProvidersRequest): Promise<ProvidersResponse> => {
  const response = await api.get<ProvidersResponse>(`/v1/providers`, {
    params: {
      types,
    },
  });
  return response.data;
};
export interface CreateProviderRequest {
  merchantId: string;
  data: {
    name: string;
    code: string;
    priority: number | string;
    methods: string[];
    credentials?: {
      type: string;
      [key: string]: string;
    };
    acquirer: {
      merchantId?: string;
      brands?: string[];
      creditBrands?: string[];
      debitBrands?: string[];
    };
    state: "Enabled" | "Disabled";
    type: string;
  };
}

export const createProvider = async ({
  merchantId,
  data,
}: CreateProviderRequest): Promise<CreateProviderResponseData> => {
  const response = await api.post<CreateProviderApiResponse>(
    `/v1/merchants/${merchantId}/providers`,
    data,
  );

  const providerId = response.data.data?.id;
  if (!providerId) {
    throw new Error("Invalid create provider response");
  }

  const additionalSteps = normalizeAdditionalStep(
    response.data.data?.additionalSteps ?? response.data.additionalSteps,
  );

  return {
    id: providerId,
    additionalSteps,
  };
};

export interface DeleteProviderRequest {
  merchantId: string;
  providerId: string;
}

export const deleteProvider = async ({
  merchantId,
  providerId,
}: DeleteProviderRequest): Promise<void> => {
  await api.delete(`/v1/merchants/${merchantId}/providers/${providerId}`);
};

export interface UpdateProviderRequest {
  merchantId: string;
  providerId: string;
  data: {
    name: string;
    code: string;
    priority: number | string;
    methods: string[];
    credentials?: {
      type: string;
      [key: string]: string;
    };
    acquirer: {
      merchantId?: string;
      brands?: string[];
      creditBrands?: string[];
      debitBrands?: string[];
    };
    state: "Enabled" | "Disabled";
  };
}

export const updateProvider = async ({
  merchantId,
  providerId,
  data,
}: UpdateProviderRequest): Promise<{ id: string }> => {
  const response = await api.put<{ id: string }>(
    `/v1/merchants/${merchantId}/providers/${providerId}`,
    data,
  );
  return response.data;
};

export interface MerchantInfo {
  data: {
    name: string;
    categoryCode: string;
    url: string;
    integrationId: string;
    syncedAt: string;
    status: string;
    id: string;
    createdAt: string;
    modifiedAt: string;
  };
}

export const getMerchantInfo = async (
  merchantId: string,
): Promise<MerchantInfo> => {
  const response = await api.get<MerchantInfo>(`/v1/merchants/${merchantId}`);
  return response.data;
};

export interface UpdateMerchantCategoryRequest {
  merchantId: string;
  categoryCode: string;
}

export const updateMerchantCategory = async ({
  merchantId,
  categoryCode,
}: UpdateMerchantCategoryRequest): Promise<MerchantInfo> => {
  const response = await api.put<MerchantInfo>(`/v1/merchants/${merchantId}`, {
    categoryCode,
  });
  return response.data;
};

export interface UpdateProviderStateRequest {
  merchantId: string;
  merchantProviderId: string;
  state: "enabled" | "disabled";
}

export const updateProviderState = async ({
  merchantId,
  merchantProviderId,
  state,
}: UpdateProviderStateRequest): Promise<void> => {
  await api.put(
    `/v1/merchants/${merchantId}/providers/${merchantProviderId}/state`,
    {
      state,
    },
  );
};

export default api;
